import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Label } from "../../../ui/label";
import { Textarea } from "../../../ui/textarea";
import { Input } from "../../../ui/input";
import { Badge } from "../../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../ui/popover";
import ReactCalendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  CalendarIcon,
  X,
  Sparkles,
  Loader2,
  Plus,
  Check,
  CheckSquare,
  Wand2,
  Flag,
  Clock,
  AlertCircle,
  Target,
} from "lucide-react";
import "./ActionItemDialog.css";
import { DateTime } from "luxon";
import { cn } from "../../../../lib/utils";
import { UserTakeaway } from "../../../../api/userTakeaways";
import { UserCategory, createCategory } from "../../../../api/userCategories";
import { suggestCategories, SuggestedCategory } from "../../../../api/insightAI";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { toast } from "sonner";
import SpaceTagPicker from "../../../shared/SpaceTagPicker";
import { useSpaces } from "../../../../context/SpaceProvider";

interface ActionItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: {
    content: string;
    priority?: "high" | "medium" | "low";
    category?: string;
    dueDate?: Date;
    spaceIds?: string[];
    isLongTermGoal?: boolean;
  }) => Promise<void>;
  categories: UserCategory[];
  item?: UserTakeaway;
  onCategoryCreated?: (category: UserCategory) => void;
  /** Pre-select a space when creating from within a space view */
  defaultSpaceId?: string;
}

// Predefined colors for new categories
const CATEGORY_COLORS = [
  "#3B82F6", // Blue
  "#10B981", // Emerald
  "#F59E0B", // Amber
  "#EF4444", // Red
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#06B6D4", // Cyan
  "#84CC16", // Lime
  "#F97316", // Orange
  "#6366F1", // Indigo
];

// Priority keywords for AI suggestion
const PRIORITY_KEYWORDS = {
  high: ["urgent", "asap", "critical", "immediately", "important", "priority", "deadline", "today", "must", "essential", "emergency"],
  medium: ["soon", "should", "need to", "this week", "next few days", "moderate"],
  low: ["eventually", "when possible", "nice to have", "sometime", "later", "low priority", "optional"],
};

const ActionItemDialog: React.FC<ActionItemDialogProps> = ({
  open,
  onOpenChange,
  onSave,
  categories,
  item,
  onCategoryCreated,
  defaultSpaceId,
}) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const { hasSpaces } = useSpaces();
  const [content, setContent] = useState("");
  const [priority, setPriority] = useState<"high" | "medium" | "low" | undefined>(undefined);
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [spaceIds, setSpaceIds] = useState<string[]>([]);
  const [isLongTermGoal, setIsLongTermGoal] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [saving, setSaving] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  // AI suggestion state
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestedCategory[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState(CATEGORY_COLORS[0]);
  const [suggestedPriority, setSuggestedPriority] = useState<"high" | "medium" | "low" | null>(null);

  // Load existing data when editing
  useEffect(() => {
    if (item) {
      setContent(item.content || "");
      setPriority(item.priority);
      setCategoryId(item.category?._id);
      setDueDate(item.dueDate ? new Date(item.dueDate) : undefined);
      setSpaceIds(item.spaceIds || []);
      setIsLongTermGoal((item as any).isLongTermGoal || false);
    } else {
      setContent("");
      setPriority(undefined);
      setCategoryId(undefined);
      setDueDate(undefined);
      setSpaceIds(defaultSpaceId ? [defaultSpaceId] : []);
      setIsLongTermGoal(false);
    }
    // Reset suggestions when dialog opens/closes
    setSuggestions([]);
    setShowSuggestions(false);
    setIsCreatingCategory(false);
    setSuggestedPriority(null);
  }, [item, open, defaultSpaceId]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);

  // Auto-suggest priority based on content
  const analyzePriority = (text: string): "high" | "medium" | "low" | null => {
    const lower = text.toLowerCase();

    for (const keyword of PRIORITY_KEYWORDS.high) {
      if (lower.includes(keyword)) return "high";
    }
    for (const keyword of PRIORITY_KEYWORDS.low) {
      if (lower.includes(keyword)) return "low";
    }
    for (const keyword of PRIORITY_KEYWORDS.medium) {
      if (lower.includes(keyword)) return "medium";
    }

    return null;
  };

  // Handle content change and auto-analyze
  const handleContentChange = (value: string) => {
    setContent(value);

    // Auto-suggest priority if not already set and content is long enough
    if (!priority && value.length > 15) {
      const detected = analyzePriority(value);
      if (detected && detected !== suggestedPriority) {
        setSuggestedPriority(detected);
      }
    }
  };

  const handleSuggestCategories = async () => {
    if (!content.trim() || content.length < 10) {
      toast.error(t("content_too_short_for_suggestion") || "Please enter more content for better suggestions");
      return;
    }

    setIsSuggesting(true);
    setSuggestions([]);

    try {
      const response = await suggestCategories(retryFetch, {
        content: content.trim(),
        existingCategoryIds: categories.map((c) => c._id),
      });

      if (response.status === "SUCCESS" && response.suggestions?.length > 0) {
        setSuggestions(response.suggestions);
        setShowSuggestions(true);
      } else {
        // Fallback to local suggestion
        const localSuggestions = suggestCategoriesLocally(content);
        setSuggestions(localSuggestions);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error("Failed to get AI suggestions, using local fallback:", error);
      const localSuggestions = suggestCategoriesLocally(content);
      setSuggestions(localSuggestions);
      setShowSuggestions(true);
    } finally {
      setIsSuggesting(false);
    }
  };

  // Local fallback for category suggestions
  const suggestCategoriesLocally = (text: string): SuggestedCategory[] => {
    const contentLower = text.toLowerCase();
    const results: SuggestedCategory[] = [];

    // First, try to match existing categories
    for (const category of categories) {
      const categoryLower = category.name.toLowerCase();
      const keywords = categoryLower.split(/\s+/);

      let matchScore = 0;
      for (const keyword of keywords) {
        if (keyword.length > 2 && contentLower.includes(keyword)) {
          matchScore += 1;
        }
      }

      if (matchScore > 0 || contentLower.includes(categoryLower)) {
        results.push({
          categoryId: category._id,
          categoryName: category.name,
          confidence: Math.min(0.9, 0.5 + matchScore * 0.2),
          isNew: false,
          reason: t("matches_existing_category") || "Matches your existing category",
        });
      }
    }

    // Suggest new categories based on action-oriented themes
    const themeKeywords: Record<string, { keywords: string[]; color: string }> = {
      "Meetings": { keywords: ["meet", "call", "schedule", "sync", "discuss", "1:1", "standup"], color: "#3B82F6" },
      "Follow-ups": { keywords: ["follow up", "check in", "reach out", "contact", "remind", "ping"], color: "#10B981" },
      "Projects": { keywords: ["project", "deliverable", "milestone", "launch", "release", "complete"], color: "#8B5CF6" },
      "Reviews": { keywords: ["review", "approve", "feedback", "check", "evaluate", "assess"], color: "#F59E0B" },
      "Documentation": { keywords: ["document", "write", "update", "create doc", "readme", "notes"], color: "#06B6D4" },
      "Research": { keywords: ["research", "investigate", "explore", "analyze", "look into", "study"], color: "#EC4899" },
      "Planning": { keywords: ["plan", "prepare", "organize", "schedule", "roadmap", "strategy"], color: "#F97316" },
      "Communication": { keywords: ["email", "message", "notify", "inform", "announce", "share"], color: "#6366F1" },
    };

    for (const [themeName, { keywords, color }] of Object.entries(themeKeywords)) {
      // Skip if we already have this category
      if (categories.some((c) => c.name.toLowerCase() === themeName.toLowerCase())) {
        continue;
      }

      const matchCount = keywords.filter((kw) => contentLower.includes(kw)).length;
      if (matchCount >= 1) {
        results.push({
          categoryName: themeName,
          confidence: Math.min(0.85, 0.4 + matchCount * 0.2),
          isNew: true,
          suggestedColor: color,
          reason: t("suggested_based_on_content") || "Suggested based on your content",
        });
      }
    }

    // Sort by confidence and return top 4
    return results.sort((a, b) => b.confidence - a.confidence).slice(0, 4);
  };

  const handleSelectSuggestion = (suggestion: SuggestedCategory) => {
    if (suggestion.isNew) {
      setNewCategoryName(suggestion.categoryName);
      setNewCategoryColor(suggestion.suggestedColor || CATEGORY_COLORS[0]);
      setIsCreatingCategory(true);
    } else if (suggestion.categoryId) {
      setCategoryId(suggestion.categoryId);
      setShowSuggestions(false);
    }
  };

  const handleCreateNewCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const response = await createCategory(retryFetch, {
        name: newCategoryName.trim(),
        color: newCategoryColor,
        type: "action",
      });

      if (response.status === "CREATED" && response.category) {
        setCategoryId(response.category._id);
        onCategoryCreated?.(response.category);
        toast.success(t("category_created") || "Category created successfully");
        setIsCreatingCategory(false);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error("Failed to create category:", error);
      toast.error(t("failed_to_create_category") || "Failed to create category");
    }
  };

  // Quick date setters
  const setQuickDate = (type: "today" | "tomorrow" | "thisWeek" | "nextWeek") => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    switch (type) {
      case "today":
        setDueDate(today);
        break;
      case "tomorrow":
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        setDueDate(tomorrow);
        break;
      case "thisWeek":
        const endOfWeek = new Date(today);
        const dayOfWeek = endOfWeek.getDay();
        const daysUntilFriday = dayOfWeek <= 5 ? 5 - dayOfWeek : 0;
        endOfWeek.setDate(endOfWeek.getDate() + daysUntilFriday);
        setDueDate(endOfWeek);
        break;
      case "nextWeek":
        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);
        setDueDate(nextWeek);
        break;
    }
    setShowCalendar(false);
  };

  const handleSave = async () => {
    if (!content.trim()) return;

    setSaving(true);
    try {
      await onSave({
        content: content.trim(),
        priority: priority || undefined,
        category: categoryId || undefined,
        dueDate: dueDate || undefined,
        spaceIds: spaceIds.length > 0 ? spaceIds : undefined,
        isLongTermGoal: isLongTermGoal || undefined,
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to save action item:", error);
    } finally {
      setSaving(false);
    }
  };

  const acceptSuggestedPriority = () => {
    if (suggestedPriority) {
      setPriority(suggestedPriority);
      setSuggestedPriority(null);
    }
  };

  const getPriorityColor = (p: string) => {
    switch (p) {
      case "high": return "text-red-600 bg-red-50 border-red-200";
      case "medium": return "text-amber-600 bg-amber-50 border-amber-200";
      case "low": return "text-green-600 bg-green-50 border-green-200";
      default: return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader className="pb-4">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200">
              <CheckSquare className="h-5 w-5 text-white" />
            </div>
            <div>
              <span className="text-lg">{item ? t("edit_action_item", "Edit Action Item") : t("add_action_item")}</span>
              <p className="text-sm font-normal text-muted-foreground mt-0.5">
                {item ? t("edit_action_item_description", "Update your task details") : t("add_action_item_description", "Create a new task to track")}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-5 py-2">
          {/* Content */}
          <div className="grid gap-2">
            <Label htmlFor="content" className="text-sm font-medium">{t("content")}</Label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => handleContentChange(e.target.value)}
              placeholder={t("enter_action_item_content", "What needs to be done?")}
              rows={3}
              className="resize-none border-2 focus:border-primary/30 rounded-xl transition-colors"
            />

            {/* Suggested Priority Alert */}
            {suggestedPriority && !priority && (
              <div className="flex items-center justify-between p-2.5 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  <span className="text-xs text-amber-700">
                    {t("suggested_priority", "Detected priority:")}
                    <Badge className={cn("ml-1.5 text-xs", getPriorityColor(suggestedPriority))}>
                      {t(suggestedPriority)}
                    </Badge>
                  </span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={acceptSuggestedPriority}
                  className="h-7 text-xs text-amber-700 hover:text-amber-800 hover:bg-amber-100"
                >
                  <Check className="h-3 w-3 mr-1" />
                  {t("accept", "Accept")}
                </Button>
              </div>
            )}
          </div>

          {/* Priority & Category Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Priority */}
            <div className="grid gap-2">
              <Label htmlFor="priority" className="text-sm font-medium flex items-center gap-1.5">
                <Flag className="h-3.5 w-3.5 text-muted-foreground" />
                {t("priority")}
              </Label>
              <Select
                value={priority || "none"}
                onValueChange={(value) => {
                  setPriority(value === "none" ? undefined : value as "high" | "medium" | "low");
                  setSuggestedPriority(null);
                }}
              >
                <SelectTrigger id="priority" className="border-2 rounded-xl">
                  <SelectValue placeholder={t("select_priority")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-muted-foreground">{t("no_priority")}</span>
                  </SelectItem>
                  <SelectItem value="high">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                      <span className="text-red-600 font-medium">{t("high")}</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="medium">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-amber-500" />
                      <span className="text-amber-600 font-medium">{t("medium")}</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="low">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-green-600 font-medium">{t("low")}</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Due Date */}
            <div className="grid gap-2">
              <Label htmlFor="dueDate" className="text-sm font-medium flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                {t("due_date")}
              </Label>
              <div className="relative">
                <Button
                  id="dueDate"
                  variant="outline"
                  onClick={() => setShowCalendar(!showCalendar)}
                  className={cn(
                    "w-full justify-start text-left font-normal border-2 rounded-xl h-10",
                    !dueDate && "text-muted-foreground"
                  )}
                  type="button"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? (
                    <span>{DateTime.fromJSDate(dueDate).toLocaleString(DateTime.DATE_MED)}</span>
                  ) : (
                    <span>{t("pick_date")}</span>
                  )}
                </Button>

                {showCalendar && (
                  <div
                    ref={calendarRef}
                    className="absolute z-50 w-[300px] right-0 p-3 mt-2 border-2 rounded-xl shadow-xl bg-background"
                  >
                    {/* Quick date buttons */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setQuickDate("today")}
                        className="h-7 text-xs rounded-lg"
                      >
                        {t("today", "Today")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setQuickDate("tomorrow")}
                        className="h-7 text-xs rounded-lg"
                      >
                        {t("tomorrow", "Tomorrow")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setQuickDate("thisWeek")}
                        className="h-7 text-xs rounded-lg"
                      >
                        {t("this_friday", "This Friday")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setQuickDate("nextWeek")}
                        className="h-7 text-xs rounded-lg"
                      >
                        {t("next_week", "Next week")}
                      </Button>
                    </div>

                    <ReactCalendar
                      onChange={(value) => {
                        setDueDate(value as Date);
                        setShowCalendar(false);
                      }}
                      value={dueDate || new Date()}
                      minDate={new Date()}
                      className="w-full border-none"
                    />
                    {dueDate && (
                      <div className="pt-3 mt-3 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-destructive hover:text-destructive hover:bg-red-50"
                          onClick={() => {
                            setDueDate(undefined);
                            setShowCalendar(false);
                          }}
                          type="button"
                        >
                          <X className="h-4 w-4 mr-2" />
                          {t("clear_date")}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Category with AI Suggestion */}
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="category" className="text-sm font-medium">{t("category")}</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleSuggestCategories}
                disabled={isSuggesting || content.length < 10}
                className="h-7 text-xs text-violet-600 hover:text-violet-700 hover:bg-violet-50 rounded-lg"
              >
                {isSuggesting ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    {t("suggesting") || "Suggesting..."}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3 w-3 mr-1" />
                    {t("suggest_category") || "Suggest Category"}
                  </>
                )}
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Select
                value={categoryId || "none"}
                onValueChange={(value) => setCategoryId(value === "none" ? undefined : value)}
              >
                <SelectTrigger id="category" className="border-2 rounded-xl flex-1">
                  <SelectValue placeholder={t("select_category")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("no_category") || "No Category"}</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat._id} value={cat._id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full border"
                          style={{ backgroundColor: cat.color }}
                        />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setIsCreatingCategory(true)}
                title={t("create_new_category") || "Create new category"}
                aria-label={t("create_new_category") || "Create new category"}
                className="h-10 w-10 shrink-0 border-2 rounded-xl text-gray-500 hover:text-violet-600 hover:border-violet-300 hover:bg-violet-50"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* AI Suggestions Panel / new-category form */}
            {((showSuggestions && suggestions.length > 0) || isCreatingCategory) && (
              <div className="mt-2 p-3 rounded-xl bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200">
                {!isCreatingCategory && (
                  <div className="flex items-center gap-2 mb-2.5">
                    <Wand2 className="h-4 w-4 text-violet-500" />
                    <span className="text-xs font-medium text-violet-700">
                      {t("ai_suggested_categories") || "AI Suggested Categories"}
                    </span>
                  </div>
                )}

                {!isCreatingCategory ? (
                  <div className="flex flex-wrap gap-2">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSelectSuggestion(suggestion)}
                        className={cn(
                          "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all",
                          "hover:ring-2 hover:ring-violet-300 hover:shadow-sm",
                          suggestion.isNew
                            ? "bg-white border border-dashed border-violet-300 text-violet-700"
                            : "bg-white border border-violet-200 text-gray-700"
                        )}
                      >
                        {!suggestion.isNew && (
                          <div
                            className="w-2.5 h-2.5 rounded-full"
                            style={{
                              backgroundColor:
                                categories.find((c) => c._id === suggestion.categoryId)?.color ||
                                suggestion.suggestedColor,
                            }}
                          />
                        )}
                        {suggestion.isNew && <Plus className="h-3 w-3 text-violet-500" />}
                        {suggestion.categoryName}
                        <Badge
                          variant="secondary"
                          className="ml-1 h-4 text-[10px] bg-violet-100 text-violet-600 border-0"
                        >
                          {Math.round(suggestion.confidence * 100)}%
                        </Badge>
                      </button>
                    ))}
                  </div>
                ) : (
                  // New category creation form
                  <div className="space-y-3">
                    <p className="text-xs text-violet-600 font-medium">
                      {t("create_new_category") || "Create new category"}
                    </p>
                    <div className="flex items-center gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            className="w-9 h-9 rounded-lg border-2 border-gray-200 hover:border-gray-300 transition-colors shadow-sm"
                            style={{ backgroundColor: newCategoryColor }}
                          />
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-2">
                          <div className="grid grid-cols-5 gap-1.5">
                            {CATEGORY_COLORS.map((color) => (
                              <button
                                key={color}
                                onClick={() => setNewCategoryColor(color)}
                                className={cn(
                                  "w-7 h-7 rounded-lg transition-transform hover:scale-110",
                                  newCategoryColor === color && "ring-2 ring-offset-2 ring-gray-400"
                                )}
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Input
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder={t("category_name") || "Category name"}
                        className="flex-1 h-9 text-sm border-2 rounded-lg"
                      />
                      <Button
                        size="sm"
                        onClick={handleCreateNewCategory}
                        disabled={!newCategoryName.trim()}
                        className="h-9 px-3 bg-violet-500 hover:bg-violet-600 rounded-lg"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsCreatingCategory(false)}
                        className="h-9 rounded-lg"
                      >
                        {t("cancel")}
                      </Button>
                    </div>
                  </div>
                )}

                {!isCreatingCategory && (
                  <p className="mt-2.5 text-[10px] text-violet-500">
                    {t("click_to_select_or_create") || "Click to select, or click + to create new"}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Identity Space assignment — only shown when user has spaces */}
          {hasSpaces && (
            <div className="grid gap-2">
              <Label className="text-sm font-medium">{t("identity_space") || "Identity Space"}</Label>
              <SpaceTagPicker
                selectedSpaceIds={spaceIds}
                onChange={setSpaceIds}
              />
            </div>
          )}
        </div>

        {/* Long-term goal toggle */}
        <div className="flex items-center gap-3 py-2 px-1">
          <button
            type="button"
            role="switch"
            aria-checked={isLongTermGoal}
            className={cn(
              "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
              isLongTermGoal ? "bg-teal-500" : "bg-muted"
            )}
            onClick={() => setIsLongTermGoal(!isLongTermGoal)}
          >
            <span
              className={cn(
                "pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform",
                isLongTermGoal ? "translate-x-4" : "translate-x-0"
              )}
            />
          </button>
          <div className="flex items-center gap-1.5">
            <Target className="w-4 h-4 text-teal-500" />
            <span className="text-sm font-medium">{t("long_term_goal", "Long-term goal")}</span>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} className="rounded-xl">
            {t("cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!content.trim() || saving}
            className="bg-gradient-to-r from-primary to-indigo-600 hover:from-primary hover:to-indigo-700 rounded-xl shadow-lg shadow-blue-200"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("saving") || "Saving..."}
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {t("save")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ActionItemDialog;
